import { createTheme, PaletteMode, ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: {
      main: string;
      dark: string;
      light: string;
      contrastText: string;
    };
  }
  interface PaletteOptions {
    neutral: {
      main: string;
      dark: string;
      light: string;
      contrastText: string;
    };
  }
}

const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    primary: {
      main: '#2563eb', // Professional blue
      light: '#60a5fa',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#475569', // Slate gray
      light: '#64748b',
      dark: '#334155',
      contrastText: '#ffffff',
    },
    neutral: {
      main: '#64748b',
      light: '#94a3b8',
      dark: '#475569',
      contrastText: '#ffffff',
    },
    ...(mode === 'light'
      ? {
          background: {
            default: '#f1f5f9',
            paper: '#ffffff',
          },
          text: {
            primary: '#1e293b',
            secondary: '#64748b',
          },
          divider: '#e2e8f0',
        }
      : {
          background: {
            default: '#0f172a',
            paper: '#1e293b',
          },
          text: {
            primary: '#f1f5f9',
            secondary: '#94a3b8',
          },
          divider: '#334155',
        }),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.75,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.57,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.75,
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 2.66,
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow:
            mode === 'light'
              ? '0px 1px 2px rgba(15, 23, 42, 0.06), 0px 1px 6px rgba(15, 23, 42, 0.04)'
              : '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 4px 12px rgba(0, 0, 0, 0.24)',
          backgroundImage: 'none',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          backgroundColor: mode === 'light' ? '#eff6ff' : '#1f2937',
          color: mode === 'light' ? '#0f172a' : '#f8fafc',
          borderBottom: `1px solid ${mode === 'light' ? '#e2e8f0' : '#334155'}`,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          '& .MuiDataGrid-columnHeaders, & .MuiDataGridPro-columnHeaders': {
            backgroundColor: mode === 'light' ? '#eff6ff' : '#1f2937',
            color: mode === 'light' ? '#0f172a' : '#f8fafc',
            fontWeight: 700,
            borderBottom: `1px solid ${mode === 'light' ? '#e2e8f0' : '#334155'}`,
          },
          '& .MuiDataGrid-columnHeader, & .MuiDataGridPro-columnHeader': {
            fontWeight: 700,
          },
          '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGridPro-columnHeaderTitle': {
            fontWeight: 700,
          },
        },
      },
    },
  },
});

export const getAppTheme = (mode: PaletteMode) => createTheme(getDesignTokens(mode));

const theme = getAppTheme('light');

export default theme;
